import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';
import { useTheme } from '@/contexts/theme';
import { useIsMobile } from '@/shared/hooks';
import { DrawerTabBar } from './DrawerTabBar';

/**
 * Item de aba do header do Drawer (F76)
 */
export interface DrawerTabItem {
  /** Identificador único da aba */
  id: string;
  /** Rótulo exibido */
  label: React.ReactNode;
  /** Ícone opcional (14px, via catálogo Icon — render pelo DrawerTabBar) */
  icon?: IconName;
}

/**
 * Config das abas do header do Drawer (controladas pelo consumidor)
 */
export interface DrawerTabs {
  /** Definição das abas */
  items: DrawerTabItem[];
  /** Id da aba ativa */
  active: string;
  /** Callback ao trocar de aba */
  onChange: (id: string) => void;
}

/**
 * Props do componente Drawer shared
 */
export interface DrawerProps {
  /** Controla se o drawer está aberto */
  isOpen: boolean;
  /** Callback para fechar o drawer */
  onClose: () => void;
  /** Tamanho do drawer (mockup .drw): md 480px, lg 720px, xl 960px (master-detail) */
  size?: 'md' | 'lg' | 'xl';
  /** Largura customizada (classe Tailwind, ex: `w-[624px]`) — sobrepõe `size` no desktop.
   *  Use quando o mockup da tela define uma largura própria fora da escala md/lg/xl. */
  widthClass?: string;
  /** Ícone no header (elemento React já estilizado) */
  icon?: React.ReactNode;
  /** Título do drawer (opcional quando customHeader é fornecido) */
  title?: string;
  /** Subtítulo opcional */
  subtitle?: string;
  /** Header customizado que substitui o header padrão (título + ícone + close button).
   *  Evitar quando o motivo for só abas — usar `tabs` (F76). Se fornecido, `tabs` é ignorado. */
  customHeader?: React.ReactNode;
  /** Abas dentro do header padrão, abaixo do título — renderizadas pelo
   *  `DrawerTabBar` (gramática `.tabs2`: largura do conteúdo, 14 semibold,
   *  sublinhado 2px). Substitui os casos de `customHeader` usados só por
   *  causa de abas (F76). */
  tabs?: DrawerTabs;
  /** Conteúdo scrollável */
  children: React.ReactNode;
  /** Conteúdo do footer (botões) */
  footer?: React.ReactNode;
  /** Bloqueia interação durante loading */
  disabled?: boolean;
}

const SIZE_MAP = {
  md: 'w-[480px]',
  lg: 'w-[720px]',
  xl: 'w-[960px]',
} as const;

/**
 * Drawer lateral direito reutilizável
 *
 * Overlay animado + container com translateX.
 * Cores hardcoded light (padrão de modais).
 * NÃO fecha ao clicar fora (superfície de conteúdo — form/detalhe/config);
 * fecha apenas pelo X, botão Cancelar/Fechar ou Esc. Ver DS §Overlays.
 * Suporta header padrão (título/ícone) ou customHeader para layouts complexos.
 *
 * @example
 * <Drawer
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   title="Meu Drawer"
 *   tabs={{
 *     items: [
 *       { id: 'general', label: 'Geral', icon: 'settings' },
 *       { id: 'members', label: 'Membros', icon: 'users' },
 *     ],
 *     active: activeTab,
 *     onChange: setActiveTab,
 *   }}
 *   footer={<Button onClick={save}>Salvar</Button>}
 * >
 *   <p>Conteúdo aqui</p>
 * </Drawer>
 */
export const Drawer: React.FC<DrawerProps> = ({
  isOpen,
  onClose,
  size = 'md',
  widthClass,
  icon,
  title = '',
  subtitle,
  customHeader,
  tabs,
  children,
  footer,
  disabled = false,
}) => {
  const { lightTokens } = useTheme();
  const lt = lightTokens.colors;
  // Mobile (< shell 1024): drawer full-width lateral (mockup `.drw{width:100vw}` /
  // `.ldrawer{width:100%}`). Modais é que viram bottom-sheet, drawers não.
  const isMobile = useIsMobile();

  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      const raf = requestAnimationFrame(() => setIsAnimating(true));
      return () => cancelAnimationFrame(raf);
    } else {
      setIsAnimating(false);
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleClose = () => {
    if (disabled) return;
    onClose();
  };

  // Fecha por Escape (mockup: `keydown Escape → closeLayers`)
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, disabled]);

  if (!shouldRender) return null;

  // Mobile (<1024): drawer full-width lateral (100vw), desliza da direita.
  // Desktop: drawer lateral direito com a largura do size.
  const panelClass = isMobile
    ? `ds-drw fixed right-0 top-0 h-full w-full flex flex-col`
    : `ds-drw fixed right-0 top-0 h-full ${widthClass ?? SIZE_MAP[size]} max-w-full flex flex-col`;

  // Portal pro document.body: sem isso, um ancestral com transform/filter/contain
  // (ex.: painel de outro Drawer, card do Kanban em drag) vira o containing block do
  // `position: fixed` e o overlay abre "por dentro"/clipado no pai. Ver DS §Overlays.
  return createPortal(
    <div
      className="fixed inset-0 z-[60]"
      style={{
        backgroundColor: isAnimating ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0)',
        transition: 'background-color 300ms ease',
      }}
      /* SEM onClick no overlay: drawers carregam form/detalhe/config — clique fora
         acidental perderia trabalho do usuário. Fecha só por X/Cancelar/Esc.
         Ver DS §Overlays "Política de dismissal". */
      onClick={(e) => e.stopPropagation()}
    >
      {/* Drawer container — force light theme CSS variables */}
      <div
        className={panelClass}
        style={{
          backgroundColor: '#ffffff',
          boxShadow: isMobile ? 'none' : '-4px 0 24px rgba(0, 0, 0, 0.12)',
          transform: isAnimating ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 300ms ease',
          // Override CSS variables to light theme (drawers are always light, like modals)
          '--theme-text': lt.text.primary,
          '--theme-muted-text': lt.text.muted,
          '--theme-input-background': lt.background.input,
          '--theme-input-text': lt.text.primary,
          '--theme-input-border': lt.border.input,
          '--theme-input-focus': lt.border.focus,
          '--theme-error-color': lt.error,
          '--theme-card-background': lt.background.card,
          '--theme-card-text': lt.text.secondary,
          '--theme-card-border': lt.border.default,
          '--theme-card-muted': lt.text.muted,
          '--theme-button-primary': lt.primary,
          '--theme-button-primary-text': lt.text.inverse,
          '--theme-button-primary-hover': lt.primaryHover,
          '--theme-button-secondary': lt.background.elevated,
          '--theme-button-secondary-text': lt.text.secondary,
          '--theme-button-secondary-hover': lt.border.strong,
          '--theme-button-ghost': 'transparent',
          '--theme-button-ghost-hover': lt.background.elevated,
          '--theme-primary': lt.primary,
          '--theme-border': lt.border.default,
          '--theme-component-background': lt.background.card,
        } as React.CSSProperties}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {customHeader ? (
          <div className="flex-shrink-0">{customHeader}</div>
        ) : (
          /* `.drw-head` do mockup: padding 15px 18px, título 16 bold, sub 12 muted,
             X 32×32 radius 8; borda #eef1f5 sob o título. Com `tabs` (F76), a
             barra de abas vem abaixo com a própria borda (gramática do drawer
             de Detalhes do Lead — duas faixas, cada uma com sua borda). */
          <div className="flex-shrink-0">
            <div className="flex items-center gap-3 border-b" style={{ padding: '15px 18px', borderColor: '#eef1f5' }}>
              {icon && (
                <div
                  className="p-2 rounded-lg flex-none"
                  style={{ backgroundColor: '#f3e8ff', color: '#8b5cf6' }}
                >
                  {icon}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <h2 className="text-[16px] font-bold whitespace-nowrap truncate" style={{ color: '#1e293b' }}>
                  {title}
                </h2>
                {subtitle && (
                  <p className="text-xs" style={{ color: '#64748b' }}>
                    {subtitle}
                  </p>
                )}
              </div>
              <button
                onClick={handleClose}
                className="w-8 h-8 flex items-center justify-center rounded-lg transition-colors flex-none"
                style={{ color: '#64748b' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f1f5f9';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                disabled={disabled}
                data-testid="drawer-close"
              >
                <Icon name="x" className="w-4 h-4" />
              </button>
            </div>
            {/* Barra de abas — DrawerTabBar (gramática `.tabs2` do drawer de
                Detalhes do Lead, padrão do DS 2026-08-11): abas com largura do
                conteúdo, 14 semibold, sublinhado 2px roxo. */}
            {tabs && tabs.items.length > 0 && (
              <DrawerTabBar
                items={tabs.items.map((tab) => ({
                  id: tab.id,
                  label: tab.label,
                  ...(tab.icon ? { icon: tab.icon } : {}),
                  testid: `drawer-tab-${tab.id}`,
                }))}
                active={tabs.active}
                onChange={tabs.onChange}
                disabled={disabled}
                data-testid="drawer-tabs"
              />
            )}
          </div>
        )}

        {/* Scrollable content — padding é responsabilidade do consumidor */}
        {/*
          ⚠️ O corpo NÃO aplica padding de propósito: drawers master-detail
          (SLA, funis, pipelines) precisam que o conteúdo encoste nas bordas
          para desenhar as duas colunas.

          Consequência: **toda tela de formulário precisa pôr o seu**. O padrão
          é `px-6 py-5` no primeiro filho — sem ele os campos colam na borda.
          Já foi esquecido em 4 drawers (workflow, formulário, webhook de agente,
          fonte de visitas), sempre com o mesmo sintoma visual.
        */}
        <div className="flex-1 overflow-y-auto">
          {children}
        </div>

        {/* `.drw-foot` do mockup: fundo branco, padding 13px 18px, gap 10, borda #eef1f5 */}
        {footer && (
          <div
            className="flex items-center justify-end border-t flex-shrink-0"
            style={{ padding: '13px 18px', gap: '10px', borderColor: '#eef1f5' }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

Drawer.displayName = 'Drawer';
